import { useRef, useCallback } from 'react';
import { getPresetUrlBySlug } from '../utils/presets';
import { buildTasksFromText } from '../utils/taskBuilders';

/**
 * Hook that provides cached preset task loading for logged-out users.
 * @returns {{ getPresetTasks: (fileId: string, slug: string) => Promise<Array> }}
 */
export function usePresetTasks() {
  const presetTasksRef = useRef(new Map());

  const getPresetTasks = useCallback(async (fileId, slug) => {
    if (!slug) return [];

    const cacheKey = `${fileId}:${slug}`;
    const cached = presetTasksRef.current.get(cacheKey);
    if (cached) return cached;

    const presetUrl = getPresetUrlBySlug(slug);
    if (!presetUrl) {
      presetTasksRef.current.set(cacheKey, []);
      return [];
    }

    try {
      const response = await fetch(presetUrl);
      const text = await response.text();
      const presetTasks = buildTasksFromText(fileId, text);
      presetTasksRef.current.set(cacheKey, presetTasks);
      return presetTasks;
    } catch (error) {
      console.error('Error loading preset tasks:', error);
      presetTasksRef.current.set(cacheKey, []);
      return [];
    }
  }, []);

  return { getPresetTasks };
}
